/**
 * Feature Flag Frontend Hook
 * React hook for checking feature flags and graceful feature degradation
 *
 * Unified Three-Layer Access Control:
 *   Layer 1 – Regulatory Phase gate (Phase 1/2/3)
 *   Layer 2 – Organisation feature flag (enabled/disabled)
 *   Layer 3 – Commercial entitlement (subscription or org-level seat policy)
 *
 * Use `isFeatureAvailable(key)` for the combined check.
 * `isFeatureEnabled` remains for local UI toggles.
 */

import { Trans } from "react-i18next";
import {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  createContext,
  ReactNode,
} from "react";
import apiClient from "@shared/services/apiClient";
import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import logger from "@shared/utils/logger";
import { isLocalDevelopmentPreview } from "@shared/config/localDevelopmentPreview";

// ---------------------------------------------------------------------------
// Types – mirror backend FeatureAccessResult (subscriptionService.ts)
// ---------------------------------------------------------------------------

/** Denial reasons returned by the backend unified access endpoint */
export type FeatureAccessDeniedReason =
  | "FEATURE_DISABLED_REGULATORY"
  | "FEATURE_DISABLED"
  | "SUBSCRIPTION_REQUIRED"
  | "SUBSCRIPTION_TIER_INSUFFICIENT"
  | "ORG_SUBSCRIPTION_MISSING"
  | "ORG_PLAN_TIER_INSUFFICIENT"
  | "ORG_SEAT_REQUIRED"
  | "ORG_SEAT_LIMIT_REACHED"
  | "ORG_USER_NOT_ASSIGNED_SEAT"
  /** FF-V2 Phase 4: org-scoped demo bypass — allowed with observability tag */
  | "DEMO_MODE_BYPASS";

/** Result of the unified three-layer feature availability check */
export interface FeatureAvailabilityResult {
  available: boolean;
  reason?: FeatureAccessDeniedReason;
  detail?: string;
  transientFailure?: boolean;
}

export interface FeatureAccessPresentation {
  title: string;
  message: string;
  variant: "warning" | "info" | "secondary";
  primaryActionLabel?: string;
  primaryActionHref?: string;
}

export function toFeatureAccessDeniedReason(
  value: unknown,
): FeatureAccessDeniedReason | undefined {
  if (
    value === "FEATURE_DISABLED_REGULATORY" ||
    value === "FEATURE_DISABLED" ||
    value === "SUBSCRIPTION_REQUIRED" ||
    value === "SUBSCRIPTION_TIER_INSUFFICIENT" ||
    value === "ORG_SUBSCRIPTION_MISSING" ||
    value === "ORG_PLAN_TIER_INSUFFICIENT" ||
    value === "ORG_SEAT_REQUIRED" ||
    value === "ORG_SEAT_LIMIT_REACHED" ||
    value === "ORG_USER_NOT_ASSIGNED_SEAT" ||
    value === "DEMO_MODE_BYPASS"
  ) {
    return value;
  }
  return undefined;
}

export function getFeatureAccessPresentation(
  result: Pick<FeatureAvailabilityResult, "reason" | "detail">,
): FeatureAccessPresentation {
  switch (result.reason) {
    case "FEATURE_DISABLED_REGULATORY":
      return {
        title: "Feature Pending Regulatory Enablement",
        message:
          result.detail ||
          "This feature is currently unavailable pending regulatory phase enablement.",
        variant: "secondary",
      };
    case "FEATURE_DISABLED":
      return {
        title: "Feature Not Enabled",
        message:
          result.detail ||
          "This feature is not enabled for your organization. Contact your organization administrator to request access.",
        variant: "warning",
      };
    case "SUBSCRIPTION_REQUIRED":
      return {
        title: "Subscription Required",
        message:
          result.detail ||
          "An active subscription is required to use this feature.",
        variant: "info",
        primaryActionLabel: "Manage Subscription",
        primaryActionHref: "/admin/billing/seats",
      };
    case "SUBSCRIPTION_TIER_INSUFFICIENT":
      return {
        title: "Plan Upgrade Required",
        message:
          result.detail ||
          "A higher subscription tier is required to use this feature.",
        variant: "info",
        primaryActionLabel: "Manage Subscription",
        primaryActionHref: "/admin/billing/seats",
      };
    case "ORG_SUBSCRIPTION_MISSING":
      return {
        title: "Subscription Required",
        message:
          result.detail ||
          "Your organization does not have an active subscription. Contact your administrator.",
        variant: "warning",
        primaryActionLabel: "Manage Subscription",
        primaryActionHref: "/admin/billing/seats",
      };
    case "ORG_PLAN_TIER_INSUFFICIENT":
      return {
        title: "Plan Upgrade Required",
        message:
          result.detail ||
          "This feature requires a higher plan tier. Contact your administrator to upgrade.",
        variant: "warning",
        primaryActionLabel: "Manage Subscription",
        primaryActionHref: "/admin/billing/seats",
      };
    case "ORG_SEAT_REQUIRED":
    case "ORG_SEAT_LIMIT_REACHED":
      return {
        title: "Seat Required",
        message:
          result.detail ||
          "Your organization's seat limit has been reached. Contact your administrator.",
        variant: "warning",
        primaryActionLabel: "Manage Subscription",
        primaryActionHref: "/admin/billing/seats",
      };
    case "ORG_USER_NOT_ASSIGNED_SEAT":
      return {
        title: "Seat Not Assigned",
        message:
          result.detail ||
          "You have not been assigned a seat for this feature. Contact your administrator.",
        variant: "info",
        primaryActionLabel: "Contact Administrator",
        primaryActionHref: "/admin/users",
      };
    default:
      return {
        title: "Feature Access Denied",
        message: result.detail || "This feature is not available.",
        variant: "warning",
      };
  }
}

interface FeatureFlag {
  feature_key: string;
  is_enabled: boolean;
  display_name: string;
  description?: string;
  config?: Record<string, any>;
}

function toFeatureArray(raw: any): FeatureFlag[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return [];

  // Support config-style payload: { key: { enabled, description, ... } }
  return Object.entries(raw).map(([feature_key, value]: [string, any]) => ({
    feature_key,
    is_enabled: Boolean(value?.enabled ?? value?.is_enabled),
    display_name: feature_key.replace(/_/g, " "),
    description: value?.description,
    config:
      value && typeof value === "object" ? value : { enabled: Boolean(value) },
  }));
}

interface FeatureFlagContextType {
  features: FeatureFlag[];
  isFeatureEnabled: (key: string) => boolean;
  getFeatureConfig: (key: string) => Record<string, any> | null;
  /** Unified three-layer check via backend (regulatory → org flag → subscription) */
  isFeatureAvailable: (key: string) => Promise<FeatureAvailabilityResult>;
  /** Batch-check multiple keys in a single request */
  checkFeaturesAvailable: (
    keys: string[],
  ) => Promise<Record<string, FeatureAvailabilityResult>>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined,
);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [features, setFeatures] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastFetchedSessionRef = useRef<string | null>(null);
  const fetchAttemptedForSessionRef = useRef<string | null>(null);
  const previousPathRef = useRef<string>(location.pathname);

  const isAuthPath = useCallback((pathname: string): boolean => {
    return (
      pathname.startsWith("/login") ||
      pathname.startsWith("/forgot-password") ||
      pathname.startsWith("/reset-password")
    );
  }, []);

  const fetchFeatures = useCallback(async (options?: { background?: boolean }) => {
    const isAuthRoute = isAuthPath(window.location.pathname);
    const background = Boolean(options?.background);

    // Skip feature lookup while auth bootstrap is unresolved.
    if (authLoading) {
      if (!background) {
        setLoading(true);
      }
      return;
    }

    // Skip feature lookup on auth/public pages or when session is unavailable.
    // This avoids noisy 401 churn during logout/session-expiry transitions.
    if (isAuthRoute || !isAuthenticated) {
      setFeatures([]);
      setLoading(false);
      return;
    }

    try {
      // Background refreshes (focus/visibility) must not flip loading — that
      // remounts gated pages (Lab Analytics, 3D) and looks like a continuous reload.
      if (!background) {
        setLoading(true);
      }
      setError(null);

      // Use stable config feature endpoint available in systemapi.
      const response = await apiClient.get("/config/features");
      const data = response.data?.data ?? response.data;
      setFeatures(toFeatureArray(data));
    } catch (err: any) {
      const status = err?.response?.status;
      // 401/403/404 can occur during auth bootstrap or backend route drift; keep
      // these out of console-error noise to avoid false-positive QA failures.
      if (status !== 401 && status !== 403 && status !== 404) {
        logger.error("Failed to fetch feature flags:", err);
      } else {
        logger.debug("Feature flag fetch skipped due to auth state:", status);
      }
      setError(null);
      if (!background) {
        setFeatures([]);
      }

      // Fallback: parse from response headers if API fails
      try {
        const headerFeatures = document
          .querySelector('meta[name="features-enabled"]')
          ?.getAttribute("content");
        if (headerFeatures) {
          const enabledKeys = headerFeatures.split(",");
          setFeatures(
            enabledKeys.map((key) => ({
              feature_key: key,
              is_enabled: true,
              display_name: key.replace(/_/g, " "),
            })),
          );
        }
      } catch (headerError) {
        // Non-fatal fallback path; avoid warning noise in runtime QA logs.
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [authLoading, isAuthenticated, isAuthPath]);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    const currentPath = location.pathname;
    const wasAuthPath = isAuthPath(previousPathRef.current);
    const isCurrentlyAuthPath = isAuthPath(currentPath);
    previousPathRef.current = currentPath;
    const currentSessionFingerprint = isAuthenticated
      ? user?.id || "authenticated"
      : null;

    if (!isAuthenticated) {
      lastFetchedSessionRef.current = null;
      fetchAttemptedForSessionRef.current = null;
      setFeatures([]);
      setLoading(false);
      return;
    }

    const tokenChanged =
      lastFetchedSessionRef.current !== currentSessionFingerprint;
    const transitionedOffAuthPage = wasAuthPath && !isCurrentlyAuthPath;
    const needsInitialLoad =
      features.length === 0 &&
      !isCurrentlyAuthPath &&
      fetchAttemptedForSessionRef.current !== currentSessionFingerprint;

    if (tokenChanged || transitionedOffAuthPage || needsInitialLoad) {
      lastFetchedSessionRef.current = currentSessionFingerprint;
      fetchAttemptedForSessionRef.current = currentSessionFingerprint;
      void fetchFeatures();
    }
  }, [
    authLoading,
    fetchFeatures,
    isAuthenticated,
    location.pathname,
    features.length,
    isAuthPath,
    user?.id,
  ]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    const refreshVisibleSession = () => {
      if (document.visibilityState === "visible") {
        void fetchFeatures({ background: true });
      }
    };

    window.addEventListener("focus", refreshVisibleSession);
    document.addEventListener("visibilitychange", refreshVisibleSession);

    return () => {
      window.removeEventListener("focus", refreshVisibleSession);
      document.removeEventListener("visibilitychange", refreshVisibleSession);
    };
  }, [authLoading, fetchFeatures, isAuthenticated]);

  const isFeatureEnabled = (key: string): boolean => {
    const feature = features.find((f) => f.feature_key === key);
    return feature?.is_enabled || false;
  };

  const getFeatureConfig = (key: string): Record<string, any> | null => {
    const feature = features.find((f) => f.feature_key === key);
    return feature?.config || null;
  };

  // In-flight request cache to avoid duplicate concurrent calls for the same key
  const availabilityCache = useRef<
    Map<string, Promise<FeatureAvailabilityResult>>
  >(new Map());

  /**
   * Unified three-layer access check via backend.
   * Calls GET /billing/feature/:key which evaluates:
   *   1. Regulatory phase gate
   *   2. Organisation feature flag
   *   3. Subscription tier
   * and returns a single answer with a denial reason when blocked.
   */
  const isFeatureAvailable = useCallback(
    async (key: string): Promise<FeatureAvailabilityResult> => {
      // De-duplicate in-flight requests
      const cached = availabilityCache.current.get(key);
      if (cached) return cached;

      const promise = (async (): Promise<FeatureAvailabilityResult> => {
        try {
          const resp = await apiClient.get(
            `/billing/feature/${encodeURIComponent(key)}`,
          );
          const d = resp.data?.data ?? resp.data;
          return {
            available: !!d?.hasAccess,
            reason: toFeatureAccessDeniedReason(d?.reason ?? d?.code),
            detail: d?.detail,
          };
        } catch (err: any) {
          // 402/403 responses include reason in the body
          const body = err?.response?.data?.data ?? err?.response?.data;
          if (body && typeof body.hasAccess === "boolean") {
            return {
              available: false,
              reason: toFeatureAccessDeniedReason(body.reason ?? body.code),
              detail: body.detail,
            };
          }
          const status =
            typeof err?.response?.status === "number"
              ? err.response.status
              : null;
          const transientFailure =
            status === null ||
            status === 408 ||
            status === 425 ||
            status === 429 ||
            status >= 500;
          logger.warn(`isFeatureAvailable failed for key: ${key}`, {
            status,
            transientFailure,
            error: err,
          });
          // Fail-open only in development; fail-closed in production
          return {
            available: process.env.NODE_ENV === "development",
            detail: transientFailure
              ? "Unable to verify feature access right now."
              : undefined,
            transientFailure:
              transientFailure && process.env.NODE_ENV !== "development",
          };
        } finally {
          // Expire cache entry after a short window so subsequent calls re-fetch
          setTimeout(() => availabilityCache.current.delete(key), 30_000);
        }
      })();

      availabilityCache.current.set(key, promise);
      return promise;
    },
    [],
  );

  /**
   * Batch-check multiple feature keys in a single GET /billing/features/batch
   * request.  Results are backfilled into the per-key availability cache so
   * subsequent individual isFeatureAvailable() calls resolve from cache
   * without additional network round-trips.
   */
  const checkFeaturesAvailable = useCallback(
    async (
      keys: string[],
    ): Promise<Record<string, FeatureAvailabilityResult>> => {
      if (keys.length === 0) return {};

      // Partition: keys already cached vs keys that need fetching
      const resultMap: Record<string, FeatureAvailabilityResult> = {};
      const uncachedKeys: string[] = [];

      for (const key of keys) {
        const cached = availabilityCache.current.get(key);
        if (cached) {
          // Resolve synchronously if already settled, otherwise await below
          resultMap[key] = await cached;
        } else {
          uncachedKeys.push(key);
        }
      }

      if (uncachedKeys.length > 0) {
        // Create a single promise for the batch so concurrent callers
        // with overlapping key sets share the same in-flight request.
        const batchPromise = (async (): Promise<
          Record<string, FeatureAvailabilityResult>
        > => {
          try {
            const resp = await apiClient.get("/billing/features/batch", {
              params: {
                features: uncachedKeys.join(","),
              },
            });
            const raw: Record<string, any> =
              resp.data?.data?.results ?? resp.data?.results ?? {};
            const mapped: Record<string, FeatureAvailabilityResult> = {};
            for (const key of uncachedKeys) {
              const d = raw[key];
              mapped[key] = d
                ? {
                    available: !!d.hasAccess,
                    reason: toFeatureAccessDeniedReason(d.reason ?? d.code),
                    detail: d.detail,
                  }
                : { available: process.env.NODE_ENV === "development" };
            }
            return mapped;
          } catch (err: any) {
            logger.warn(
              "checkFeaturesAvailable batch failed, falling back to individual",
              err,
            );
            // Fall back to individual calls on batch failure
            const fallback: Record<string, FeatureAvailabilityResult> = {};
            await Promise.all(
              uncachedKeys.map(async (key) => {
                fallback[key] = await isFeatureAvailable(key);
              }),
            );
            return fallback;
          }
        })();

        // Backfill per-key cache entries so isFeatureAvailable() hits cache
        for (const key of uncachedKeys) {
          const perKeyPromise = batchPromise.then(
            (m) =>
              m[key] ?? { available: process.env.NODE_ENV === "development" },
          );
          availabilityCache.current.set(key, perKeyPromise);
          setTimeout(() => availabilityCache.current.delete(key), 30_000);
        }

        const batchResults = await batchPromise;
        Object.assign(resultMap, batchResults);
      }

      return resultMap;
    },
    [isFeatureAvailable],
  );

  const value: FeatureFlagContextType = {
    features,
    isFeatureEnabled,
    getFeatureConfig,
    isFeatureAvailable,
    checkFeaturesAvailable,
    loading,
    error,
    refresh: fetchFeatures,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access feature flags
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider",
    );
  }
  return context;
};

/**
 * Hook to check if a specific feature is enabled
 */
export const useFeatureFlag = (key: string) => {
  const { isFeatureEnabled, getFeatureConfig, isFeatureAvailable, loading } =
    useFeatureFlags();

  return {
    enabled: isFeatureEnabled(key),
    config: getFeatureConfig(key),
    /** Three-layer backend access check (regulatory + org flag + subscription). */
    checkAvailability: () => isFeatureAvailable(key),
    loading,
  };
};

/**
 * HOC to conditionally render components based on feature flags
 */
interface FeatureGateProps {
  feature: string;
  requiredTier?: string;
  fallback?: ReactNode;
  children: ReactNode;
}

export const FeatureGate = ({
  feature,
  fallback = null,
  children,
}: FeatureGateProps) => {
  const { isFeatureAvailable, loading } = useFeatureFlags();
  const [result, setResult] = React.useState<FeatureAvailabilityResult | null>(
    null,
  );
  const [checking, setChecking] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setChecking(true);
    isFeatureAvailable(feature).then((r) => {
      if (!cancelled) {
        setResult(r);
        setChecking(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [feature, isFeatureAvailable]);

  if (loading || checking) {
    return (
      <div
        className="spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      />
    );
  }

  // Feature is available — render children
  if (result?.available) {
    return <>{children}</>;
  }

  if (result?.reason === "FEATURE_DISABLED_REGULATORY") {
    return <>{fallback ?? null}</>;
  }

  const presentation = getFeatureAccessPresentation(result || {});
  return (
    <>
      {fallback || (
        <div className={`alert alert-${presentation.variant}`} role="alert">
          <strong>{presentation.title}</strong>
          <p className="mb-0 mt-1">{presentation.message}</p>
          {presentation.primaryActionHref && presentation.primaryActionLabel ? (
            <div className="mt-2">
              <a
                href={presentation.primaryActionHref}
                className="btn btn-primary btn-sm"
              >
                {presentation.primaryActionLabel}
              </a>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
};

/**
 * Component to show feature upgrade prompts
 */
interface FeatureUpgradePromptProps {
  feature: string;
  requiredTier: string;
  className?: string;
}

export const FeatureUpgradePrompt = ({
  feature,
  requiredTier,
  className = "alert alert-info",
}: FeatureUpgradePromptProps) => {
  const { getFeatureConfig } = useFeatureFlags();
  const config = getFeatureConfig(feature);

  return (
    <div className={className}>
      <h6><Trans ns="features" i18nKey="ui.copy.fbafa230cbb8" /></h6>
      <p>
        <Trans ns="features" i18nKey="ui.copy.488146e6bacd" />{requiredTier} <Trans ns="features" i18nKey="ui.copy.893a24654fe5" />{config?.display_name && ` Upgrade to access ${config.display_name}.`}
      </p>
      <button className="btn btn-primary btn-sm"><Trans ns="features" i18nKey="ui.copy.935af2c98463" /></button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Regulatory Phase Gating
// ---------------------------------------------------------------------------

/** Phase numbers matching systemapi/src/config/regulatoryPhases.ts */
type RegulatoryPhase = 1 | 2 | 3;

const PHASE_SENTINEL_FEATURES: Record<2 | 3, string> = {
  2: "STATISTICAL_ANALYSIS",
  3: "ICU_EMU_MONITORING",
};

/**
 * Hook to check regulatory phase status.
 * A phase is considered enabled if ALL of its features are enabled globally.
 */
export const useRegulatoryPhase = () => {
  const { checkFeaturesAvailable, loading } = useFeatureFlags();
  const [phaseState, setPhaseState] = React.useState<
    Record<RegulatoryPhase, boolean>
  >({
    1: true,
    2: true,
    3: true,
  });
  const [phaseLoading, setPhaseLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    const evaluatePhases = async () => {
      if (loading) {
        setPhaseLoading(true);
        return;
      }

      try {
        const results = await checkFeaturesAvailable([
          PHASE_SENTINEL_FEATURES[2],
          PHASE_SENTINEL_FEATURES[3],
        ]);
        if (cancelled) return;

        const phase2Result = results[PHASE_SENTINEL_FEATURES[2]];
        const phase3Result = results[PHASE_SENTINEL_FEATURES[3]];
        setPhaseState({
          1: true,
          2: phase2Result?.reason !== "FEATURE_DISABLED_REGULATORY",
          3: phase3Result?.reason !== "FEATURE_DISABLED_REGULATORY",
        });
      } finally {
        if (!cancelled) {
          setPhaseLoading(false);
        }
      }
    };

    void evaluatePhases();

    return () => {
      cancelled = true;
    };
  }, [checkFeaturesAvailable, loading]);

  const isPhaseEnabled = (phase: RegulatoryPhase): boolean => phaseState[phase];

  return {
    phase1Enabled: true,
    phase2Enabled: phaseState[2],
    phase3Enabled: phaseState[3],
    isPhaseEnabled,
    loading: loading || phaseLoading,
  };
};

/**
 * Gate component that blocks rendering when a regulatory phase is disabled.
 * Shows a regulatory clearance message instead of the gated content.
 */
interface RegulatoryPhaseGateProps {
  phase: RegulatoryPhase;
  fallback?: ReactNode;
  children: ReactNode;
}

export const RegulatoryPhaseGate = ({
  phase,
  fallback,
  children,
}: RegulatoryPhaseGateProps) => {
  const { isPhaseEnabled, loading } = useRegulatoryPhase();
  const localPreview = isLocalDevelopmentPreview();

  if (loading) {
    return (
      <div
        className="spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      />
    );
  }

  // FF-V2 Phase 2: compliance-hidden features must not render placeholder cards
  // in clinical production. Render fallback if provided, otherwise null (hide).
  if (!isPhaseEnabled(phase)) {
    if (localPreview) {
      return (
        <>
          <div className="alert alert-warning" role="status">
            Development preview: Phase {phase} is disabled. Server-side access remains enforced.
          </div>
          {children}
        </>
      );
    }
    return <>{fallback ?? null}</>;
  }

  return <>{children}</>;
};

// UI-V2-001: Per-feature result from the unified three-layer backend check.
export interface AdvancedFeatureAvailability {
  available: boolean;
  reason?: FeatureAccessDeniedReason;
  loading: boolean;
}

const ADVANCED_FEATURE_KEYS = [
  "STATISTICAL_ANALYSIS",
  "SIGNAL_QUALITY_MONITORING",
  "ICU_EMU_MONITORING",
  "QA_DASHBOARD",
  "ADVANCED_ANALYTICS",
  "REAL_TIME_COLLABORATION",
  "3D_VISUALIZATION",
] as const;

type AdvancedFeatureKey = (typeof ADVANCED_FEATURE_KEYS)[number];

export interface AdvancedAnalyticsFeaturesResult {
  statisticalAnalysis: AdvancedFeatureAvailability;
  signalQualityMonitoring: AdvancedFeatureAvailability;
  icuEmuMonitoring: AdvancedFeatureAvailability;
  qaDashboard: AdvancedFeatureAvailability;
  advancedAnalytics: AdvancedFeatureAvailability;
  realTimeCollaboration: AdvancedFeatureAvailability;
  visualization3D: AdvancedFeatureAvailability;
  /** True until all seven backend checks have resolved. */
  loading: boolean;
}

const KEY_TO_FIELD: Record<
  AdvancedFeatureKey,
  keyof Omit<AdvancedAnalyticsFeaturesResult, "loading">
> = {
  STATISTICAL_ANALYSIS: "statisticalAnalysis",
  SIGNAL_QUALITY_MONITORING: "signalQualityMonitoring",
  ICU_EMU_MONITORING: "icuEmuMonitoring",
  QA_DASHBOARD: "qaDashboard",
  ADVANCED_ANALYTICS: "advancedAnalytics",
  REAL_TIME_COLLABORATION: "realTimeCollaboration",
  "3D_VISUALIZATION": "visualization3D",
};

const LOADING_PLACEHOLDER: AdvancedFeatureAvailability = {
  available: false,
  loading: true,
};

function buildInitialState(): AdvancedAnalyticsFeaturesResult {
  return {
    statisticalAnalysis: LOADING_PLACEHOLDER,
    signalQualityMonitoring: LOADING_PLACEHOLDER,
    icuEmuMonitoring: LOADING_PLACEHOLDER,
    qaDashboard: LOADING_PLACEHOLDER,
    advancedAnalytics: LOADING_PLACEHOLDER,
    realTimeCollaboration: LOADING_PLACEHOLDER,
    visualization3D: LOADING_PLACEHOLDER,
    loading: true,
  };
}

/**
 * Hook for advanced analytics and monitoring feature bundle.
 *
 * UI-V2-001: Uses the unified three-layer backend check (regulatory → org flag →
 * subscription) via `isFeatureAvailable` for every feature. The local
 * Local feature-config checks are intentionally NOT used here —
 * they inspect only the org config payload fetched at login and cannot enforce
 * regulatory Phase or subscription gates.
 */
export const useAdvancedAnalyticsFeatures =
  (): AdvancedAnalyticsFeaturesResult => {
    const { isFeatureAvailable } = useFeatureFlags();
    const [state, setState] =
      React.useState<AdvancedAnalyticsFeaturesResult>(buildInitialState);

    React.useEffect(() => {
      let cancelled = false;

      const evaluate = async () => {
        setState(buildInitialState());

        const results = await Promise.all(
          ADVANCED_FEATURE_KEYS.map((key) =>
            isFeatureAvailable(key).then((r) => ({ key, r })),
          ),
        );

        if (cancelled) return;

        setState((prev) => {
          const next = { ...prev, loading: false };
          for (const { key, r } of results) {
            const field = KEY_TO_FIELD[key];
            next[field] = {
              available: r.available,
              reason: r.reason,
              loading: false,
            };
          }
          return next;
        });
      };

      void evaluate();
      return () => {
        cancelled = true;
      };
    }, [isFeatureAvailable]);

    return state;
  };

export default {
  FeatureFlagProvider,
  useFeatureFlags,
  useFeatureFlag,
  FeatureGate,
  FeatureUpgradePrompt,
  useRegulatoryPhase,
  RegulatoryPhaseGate,
  useAdvancedAnalyticsFeatures,
};
